import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Zap, Infinity as InfinityIcon, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const rp = (n) => `Rp ${Number(n).toLocaleString('id-ID')}`;

/**
 * PayAsYouGoCard — buy extra AI message credits that never expire.
 * Credits are consumed automatically once the monthly or daily plan quota runs out.
 */
export const PayAsYouGoCard = ({ token }) => {
  const headers = { Authorization: `Bearer ${token}` };
  const [packs, setPacks] = useState([]);
  const [credits, setCredits] = useState(0);
  const [buying, setBuying] = useState(null);

  useEffect(() => {
    if (!token) return;
    axios.get(`${API_URL}/api/payment/duitku/topup/packs`, { headers })
      .then(res => setPacks(res.data.packs || []))
      .catch(() => {});
    axios.get(`${API_URL}/api/payment/duitku/topup/balance`, { headers })
      .then(res => setCredits(res.data.credits || 0))
      .catch(() => {});
  }, [token]);

  const buy = async (pack) => {
    setBuying(pack.id);
    try {
      const res = await axios.post(
        `${API_URL}/api/payment/duitku/topup/create`,
        { pack_id: pack.id },
        { headers }
      );
      if (res.data?.payment_url) {
        window.location.href = res.data.payment_url;
      } else {
        toast.error('Gagal membuat pembayaran');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal membuat pembayaran');
    } finally {
      setBuying(null);
    }
  };

  if (!packs.length) return null;

  return (
    <div className="bg-white dark:bg-zinc-900 rounded-xl border border-slate-200 dark:border-zinc-800 p-6" data-testid="pay-as-you-go-card">
      <div className="flex items-start justify-between mb-2 gap-3 flex-wrap">
        <h2 className="text-lg font-semibold text-slate-900 dark:text-zinc-50 flex items-center gap-2 whitespace-nowrap">
          <Zap className="w-5 h-5 text-emerald-500 shrink-0" />
          Pay As You Go
        </h2>
        <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800 shrink-0">
          <InfinityIcon className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400 shrink-0" />
          <span className="text-xs font-medium text-emerald-700 dark:text-emerald-300 tabular-nums whitespace-nowrap" data-testid="topup-credit-balance">
            {credits.toLocaleString('id-ID')} kredit pesan
          </span>
        </div>
      </div>
      <p className="text-sm text-slate-500 dark:text-zinc-400 mb-6">
        Kuota bulanan habis tapi belum mau upgrade? Beli kredit pesan tambahan — aktif seketika,
        <span className="font-medium text-slate-700 dark:text-zinc-300"> tidak hangus</span>, dan otomatis dipakai
        begitu kuota paket Anda habis.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {packs.map(pack => (
          <div
            key={pack.id}
            className={`relative rounded-xl border-2 p-4 flex flex-col transition-colors ${
              pack.popular
                ? 'border-emerald-500 bg-emerald-50/40 dark:bg-emerald-950/20'
                : 'border-slate-200 dark:border-zinc-800 hover:border-slate-400 dark:hover:border-slate-500'
            }`}
            data-testid={`topup-pack-${pack.id}`}
          >
            {pack.popular && (
              <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-emerald-600 text-white text-[10px] px-2 py-0.5 rounded-full font-medium whitespace-nowrap">
                PALING LAKU
              </span>
            )}
            {pack.best_value && (
              <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-amber-600 text-white text-[10px] px-2 py-0.5 rounded-full font-medium whitespace-nowrap">
                PALING HEMAT
              </span>
            )}
            <p className="text-sm font-semibold text-slate-900 dark:text-zinc-50">{pack.label}</p>
            <p className="text-xl font-bold text-slate-900 dark:text-zinc-50 mt-1">{rp(pack.price)}</p>
            <p className="text-xs text-slate-500 dark:text-zinc-400 mt-0.5">
              {rp(Math.round(pack.price / pack.messages))} / pesan
            </p>
            <Button
              size="sm"
              className="mt-4 w-full h-9 text-xs rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white"
              disabled={buying !== null}
              onClick={() => buy(pack)}
              data-testid={`topup-buy-${pack.id}`}
            >
              {buying === pack.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Beli Kredit'}
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PayAsYouGoCard;
